/**
 * Ask Eliza — floating assistant for the Cashflow V2 applet.
 *
 * UX matches the seer_v1 prototype (floating bubble + right-side slide-out)
 * but is wired to the real `/api/cashflow-v2/ask` endpoint. The endpoint
 * prefers the tenant's configured LLM provider (ModelService) and falls
 * back to the seeded FAQ in demo / offline mode.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { AlertCircle, Send, Sparkles, X } from 'lucide-react'

import {
  useAskEliza,
  useAskElizaSuggestions,
  type AskElizaHistoryMessage,
} from '../api'

interface ChatMessage {
  id: number
  role: 'user' | 'assistant'
  content: string
  displayedText?: string
  sources?: string[]
  confidence?: number
  model?: string
  provider?: string
  isTyping?: boolean
  errored?: boolean
}

const SOURCE_COLORS: Record<string, string> = {
  facility_config: 'bg-teal-500/20 text-teal-600 dark:text-teal-300',
  credit_agreement_2024: 'bg-purple-500/20 text-purple-600 dark:text-purple-300',
  'credit_agreement_2024.pdf':
    'bg-purple-500/20 text-purple-600 dark:text-purple-300',
  ar_aging: 'bg-amber-500/20 text-amber-600 dark:text-amber-300',
  revenue_detail: 'bg-blue-500/20 text-blue-600 dark:text-blue-300',
  weekly_forecast: 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-300',
  availability_summary: 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-300',
  variance_detail: 'bg-rose-500/20 text-rose-600 dark:text-rose-300',
  variance_narratives: 'bg-orange-500/20 text-orange-600 dark:text-orange-300',
  actuals: 'bg-indigo-500/20 text-indigo-600 dark:text-indigo-300',
  ap_aging: 'bg-pink-500/20 text-pink-600 dark:text-pink-300',
  working_capital_recommendations:
    'bg-lime-500/20 text-lime-700 dark:text-lime-300',
  actions: 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-300',
  monthly_rollup: 'bg-teal-500/20 text-teal-600 dark:text-teal-300',
  daily_bank_balances: 'bg-sky-500/20 text-sky-600 dark:text-sky-300',
  forecast_drift: 'bg-red-500/20 text-red-600 dark:text-red-300',
  acquisition_targets:
    'bg-violet-500/20 text-violet-700 dark:text-violet-300',
  acquisition_ai_assessments:
    'bg-fuchsia-500/20 text-fuchsia-700 dark:text-fuchsia-300',
  uncleared_adjustments:
    'bg-slate-500/20 text-slate-600 dark:text-slate-300',
}

function getSourceColor(source: string): string {
  return (
    SOURCE_COLORS[source] ??
    'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  )
}

function renderMarkdownBold(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>
    }
    return <span key={i}>{part}</span>
  })
}

let MESSAGE_ID = 0

export default function AskElizaDrawer() {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const typingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const suggestionsQuery = useAskElizaSuggestions()
  const askMutation = useAskEliza()

  // ── lifecycle helpers ──────────────────────────────────────────────────

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (isOpen) inputRef.current?.focus()
  }, [isOpen])

  useEffect(
    () => () => {
      if (typingIntervalRef.current) clearInterval(typingIntervalRef.current)
    },
    [],
  )

  // ── send flow ──────────────────────────────────────────────────────────

  const appendAssistant = useCallback(
    (payload: {
      answer: string
      sources: string[]
      confidence: number
      model: string
      provider: string
      errored?: boolean
    }) => {
      const id = ++MESSAGE_ID
      const fullText = payload.answer
      setMessages(prev => [
        ...prev,
        {
          id,
          role: 'assistant',
          content: fullText,
          displayedText: '',
          sources: payload.sources,
          confidence: payload.confidence,
          model: payload.model,
          provider: payload.provider,
          isTyping: true,
          errored: payload.errored,
        },
      ])
      // Gentle "typing" reveal so long LLM answers don't pop in all at once.
      const words = fullText.split(/(\s+)/)
      let wordIndex = 0
      typingIntervalRef.current = setInterval(() => {
        wordIndex += 1
        const revealed = words.slice(0, wordIndex).join('')
        setMessages(prev =>
          prev.map(msg =>
            msg.id === id
              ? {
                  ...msg,
                  displayedText: revealed,
                  isTyping: wordIndex < words.length,
                }
              : msg,
          ),
        )
        if (wordIndex >= words.length && typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current)
          typingIntervalRef.current = null
        }
      }, 40)
    },
    [],
  )

  const handleSend = useCallback(
    (rawQuestion: string) => {
      const question = rawQuestion.trim()
      if (!question || askMutation.isPending) return

      setInput('')
      const userId = ++MESSAGE_ID
      // snapshot history BEFORE appending the new user message so the server
      // doesn't receive the current question twice.
      const historyForServer: AskElizaHistoryMessage[] = messages
        .filter(m => !m.isTyping)
        .map(m => ({
          role: m.role,
          content: m.content,
        }))

      setMessages(prev => [
        ...prev,
        { id: userId, role: 'user', content: question },
      ])

      askMutation.mutate(
        { question, history: historyForServer },
        {
          onSuccess: data => appendAssistant(data),
          onError: error => {
            appendAssistant({
              answer:
                (error as Error).message ||
                'Sorry — I couldn\'t reach the assistant. Try again in a moment.',
              sources: [],
              confidence: 0,
              model: '',
              provider: '',
              errored: true,
            })
          },
        },
      )
    },
    [askMutation, appendAssistant, messages],
  )

  const handleClear = useCallback(() => {
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current)
      typingIntervalRef.current = null
    }
    setMessages([])
    setInput('')
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend(input)
    }
  }

  const suggestions = suggestionsQuery.data ?? []
  const showSuggestions = messages.length === 0 && suggestions.length > 0
  const isResponding =
    askMutation.isPending ||
    messages.some(msg => msg.isTyping && msg.role === 'assistant')

  return (
    <>
      {/* Floating Button */}
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        aria-label="Ask Eliza"
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 rounded-full bg-teal-500 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
      >
        <Sparkles size={18} />
        <span className="text-sm">Ask Eliza</span>
      </button>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20"
          onClick={() => setIsOpen(false)}
          aria-hidden
        />
      )}

      {/* Slide-out Panel */}
      <div
        role="dialog"
        aria-label="Ask Eliza conversation"
        className="fixed top-0 right-0 z-50 h-full w-[420px] max-w-full bg-white dark:bg-dark-surface shadow-2xl flex flex-col transition-transform duration-300 ease-in-out border-l border-gray-200 dark:border-dark-border"
        style={{ transform: isOpen ? 'translateX(0)' : 'translateX(100%)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-dark-border">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-teal-500" />
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Ask Eliza
              </h2>
              <p className="text-[10px] text-gray-500 dark:text-gray-400">
                Powered by the platform LLM, grounded on 13-week V2 data
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleClear}
              className="text-xs px-2 py-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-dark-hover hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              aria-label="Close Ask Eliza"
              className="p-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-dark-hover hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Message List */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {messages.length === 0 && (
            <div className="text-center text-xs text-gray-500 dark:text-gray-400 pt-10">
              Ask about covenants, entities, variances, acquisitions — answers
              cite the V2 tables they come from.
            </div>
          )}
          {messages.map(msg => (
            <div
              key={msg.id}
              className={`flex ${
                msg.role === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              {msg.role === 'user' ? (
                <div className="max-w-[85%] px-3 py-2 rounded-lg bg-teal-500/15 text-gray-900 dark:text-gray-100 text-sm">
                  {msg.content}
                </div>
              ) : (
                <div
                  className={`max-w-[95%] px-3 py-2 rounded-lg text-sm space-y-2 ${
                    msg.errored
                      ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
                      : 'bg-gray-100 dark:bg-dark-bg text-gray-900 dark:text-gray-100'
                  }`}
                >
                  <div className="leading-relaxed whitespace-pre-line flex items-start gap-2">
                    {msg.errored ? (
                      <AlertCircle size={14} className="mt-0.5 shrink-0" />
                    ) : null}
                    <span>
                      {renderMarkdownBold(msg.displayedText ?? msg.content)}
                      {msg.isTyping && (
                        <span className="inline-block w-1.5 h-4 bg-teal-500 ml-0.5 animate-pulse rounded-sm align-middle" />
                      )}
                    </span>
                  </div>

                  {!msg.isTyping && msg.sources && msg.sources.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {msg.sources.map(source => (
                        <span
                          key={source}
                          className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${getSourceColor(source)}`}
                        >
                          {source}
                        </span>
                      ))}
                    </div>
                  )}

                  {!msg.isTyping &&
                    msg.confidence != null &&
                    msg.confidence > 0 && (
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 flex items-center gap-2">
                        <span>
                          Confidence: {Math.round(msg.confidence * 100)}%
                        </span>
                        {msg.provider ? (
                          <>
                            <span aria-hidden>·</span>
                            <span>
                              {msg.provider === 'cashflow_v2_faq'
                                ? 'Demo FAQ'
                                : `${msg.provider}${msg.model ? ` · ${msg.model}` : ''}`}
                            </span>
                          </>
                        ) : null}
                      </div>
                    )}
                </div>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Suggestion Chips */}
        {showSuggestions && (
          <div className="px-4 pb-2 flex flex-wrap gap-2">
            {suggestions.map(chip => (
              <button
                key={chip}
                type="button"
                onClick={() => handleSend(chip)}
                disabled={isResponding}
                className="px-3 py-1.5 rounded-full border border-gray-200 dark:border-dark-border text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-hover hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50 transition-colors"
              >
                {chip}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-dark-border">
          <div className="flex items-center gap-2 bg-gray-50 dark:bg-dark-bg rounded-lg px-3 py-2 border border-gray-200 dark:border-dark-border focus-within:border-teal-400 transition-colors">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything about your cash flow…"
              className="flex-1 bg-transparent text-sm placeholder:text-gray-400 text-gray-900 dark:text-gray-100 outline-none"
              disabled={isResponding}
            />
            <button
              type="button"
              onClick={() => handleSend(input)}
              disabled={!input.trim() || isResponding}
              aria-label="Send message"
              className="p-1.5 rounded-md text-teal-500 hover:bg-teal-500/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
